"use client";

import { useWorkspace, PaneViewType, Task, WorkspaceFile } from "@/lib/workspace-context";
import SimpleEditor from "@/features/editor/SimpleEditor";
import GraphView from "@/features/graph/GraphView";
import DashboardPage from "./dashboard/page";
import {
  X,
  Plus,
  Hash,
  Settings,
  BookOpen,
  FileText,
  HelpCircle,
  Database,
  Eye,
  Trash2,
  Check,
  Columns,
  Network,
  Users,
  Calendar,
  Layers,
  ArrowRight,
  PlusCircle,
  Activity,
  UserCheck,
  FileSignature,
  Video,
  Sparkles,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { Suspense, useState, useRef, useEffect, useMemo } from "react";
import Link from "next/link";
import { useMeetingsStore } from "@/stores/meetings-store";
import { ConfirmDialog } from "@/components/ui/confirm-dialog";
import { ResizablePanes } from "@/components/resizable-panes";
import dynamic from "next/dynamic";

// Dynamically import force graph and WebModeView to avoid SSR crashes
const ForceGraph2D = dynamic(() => import("react-force-graph-2d"), {
  ssr: false,
});
const WebModeView = dynamic(() => import("@/features/graph/web-mode-view").then((mod) => mod.WebModeView), {
  ssr: false,
});

export default function WorkspacePage() {
  const { 
    currentViewMode, 
    isSplit 
  } = useWorkspace();

  return (
    <div className="flex-1 flex flex-col min-w-0 h-full overflow-hidden bg-background">
      {/* Main View Area */}
      <div className="flex-1 flex min-w-0 overflow-hidden relative">
        <Suspense fallback={null}>
          {currentViewMode === "dashboard" ? (
            <div className="flex-1 h-full overflow-y-auto custom-scrollbar">
              <DashboardPage />
            </div>
          ) : currentViewMode === "web" ? (
            <WebModeView />
          ) : isSplit ? (
            <ResizablePanes
              left={<PaneContainer pane="left" />}
              right={<PaneContainer pane="right" />}
              storageKey="wiki-pane-split-normal"
            />
          ) : (
            <div className="flex-1 flex min-w-0 h-full overflow-hidden">
              <PaneContainer pane="left" />
            </div>
          )}
        </Suspense>
      </div>
    </div>
  );
}

function PaneContainer({ pane }: { pane: "left" | "right" }) {
  const { leftPane, rightPane } = useWorkspace();
  const paneState = pane === "left" ? leftPane : rightPane;

  switch (paneState.viewType) {
    case "graph":
      return <GraphPaneWrapper pane={pane} />;
    case "settings":
      return <SettingsPaneWrapper pane={pane} />;
    default:
      return <SimpleEditor pane={pane} />;
  }
}

/* ═══════════════════════════════════════════════════════════════════
   MODO NORMAL: Graph & Settings wrappers
   ═══════════════════════════════════════════════════════════════════ */
function GraphPaneWrapper({ pane }: { pane: "left" | "right" }) {
  const { 
    files, 
    leftPane, 
    rightPane, 
    setActivePane, 
    setActiveTab, 
    setPaneViewType, 
    closeFile, 
    createFile, 
    openFile, 
    isSplit, 
    setIsSplit 
  } = useWorkspace();
  
  const paneState = pane === "left" ? leftPane : rightPane;

  return (
    <div 
      className={cn(
        "flex-1 flex flex-col min-w-0 h-full bg-background transition-all border-r border-border/10",
        pane === "left" ? "" : "border-l border-border/10"
      )}
      onClick={() => setActivePane(pane)}
    >
      {/* Tab bar */}
      <div className="h-9 flex items-center bg-secondary/20 border-b border-border/40 select-none overflow-x-auto custom-scrollbar shrink-0">
        <div className="flex items-center flex-1">
          {paneState.openTabs.map(tabId => {
            const f = files.find(file => file.id === tabId);
            if (!f) return null;
            return (
              <div
                key={tabId}
                className="h-9 px-3 flex items-center gap-1.5 border-r border-border/40 text-xs font-medium cursor-pointer text-muted-foreground hover:bg-secondary/40 hover:text-foreground shrink-0"
                onClick={() => {
                  setPaneViewType("editor", pane);
                  setActiveTab(tabId, pane);
                }}
              >
                <FileText className="w-3.5 h-3.5 text-muted-foreground/60" />
                <span>{f.name.replace(".md", "")}</span>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    closeFile(tabId, pane);
                  }}
                  className="p-0.5 rounded-full hover:bg-secondary text-muted-foreground hover:text-foreground transition-all ml-1.5"
                >
                  <X className="w-3 h-3" />
                </button>
              </div>
            );
          })}
          
          {/* Active Graph Tab */}
          <div className="h-9 px-3 flex items-center gap-1.5 border-r border-border/40 text-xs font-medium cursor-pointer bg-background text-foreground border-b-2 border-b-primary shrink-0">
            <Hash className="w-3.5 h-3.5 text-primary" />
            <span>Vista de Red</span>
            <button
              onClick={() => setPaneViewType(paneState.openTabs.length > 0 ? "editor" : "welcome", pane)}
              className="p-0.5 rounded-full hover:bg-secondary text-muted-foreground hover:text-foreground transition-all ml-1.5"
            >
              <X className="w-3 h-3" />
            </button>
          </div>
        </div>

        {/* Tab actions */}
        <div className="flex items-center gap-1 px-2 border-l border-border/40 shrink-0">
          <button 
            onClick={() => {
              const newId = createFile("Nota sin título");
              openFile(newId, pane);
            }}
            className="p-1 hover:bg-secondary/50 rounded text-muted-foreground hover:text-foreground transition-all"
            title="Nueva nota"
          >
            <Plus className="w-4 h-4" />
          </button>
          
          <button 
            onClick={() => setIsSplit(!isSplit)}
            className={cn(
              "p-1 hover:bg-secondary/50 rounded transition-all",
              isSplit ? "text-primary bg-primary/10" : "text-muted-foreground hover:text-foreground"
            )}
            title={isSplit ? "Unir paneles" : "Dividir panel"}
          >
            <Columns className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Graph Content */}
      <div className="flex-1 p-6 overflow-hidden">
        <GraphView />
      </div>
    </div>
  );
}

function SettingsPaneWrapper({ pane }: { pane: "left" | "right" }) {
  const { 
    files, 
    leftPane, 
    rightPane, 
    setActivePane, 
    setActiveTab, 
    setPaneViewType, 
    closeFile, 
    createFile, 
    openFile, 
    isSplit, 
    setIsSplit 
  } = useWorkspace();
  
  const paneState = pane === "left" ? leftPane : rightPane;
  const [editorFont, setEditorFont] = useState("font-sans");
  const [fontSize, setFontSize] = useState("14");
  const [successMsg, setSuccessMsg] = useState(false);
  const [showResetConfirm, setShowResetConfirm] = useState(false);

  const handleSaveSettings = () => {
    setSuccessMsg(true);
    setTimeout(() => setSuccessMsg(false), 2000);
  };

  return (
    <div 
      className={cn(
        "flex-1 flex flex-col min-w-0 h-full bg-background transition-all border-r border-border/10",
        pane === "left" ? "" : "border-l border-border/10"
      )}
      onClick={() => setActivePane(pane)}
    >
      {/* Tab bar */}
      <div className="h-9 flex items-center bg-secondary/20 border-b border-border/40 select-none overflow-x-auto custom-scrollbar shrink-0">
        <div className="flex items-center flex-1">
          {paneState.openTabs.map(tabId => {
            const f = files.find(file => file.id === tabId);
            if (!f) return null;
            return (
              <div
                key={tabId}
                className="h-9 px-3 flex items-center gap-1.5 border-r border-border/40 text-xs font-medium cursor-pointer text-muted-foreground hover:bg-secondary/40 hover:text-foreground shrink-0"
                onClick={() => {
                  setPaneViewType("editor", pane);
                  setActiveTab(tabId, pane);
                }}
              >
                <FileText className="w-3.5 h-3.5 text-muted-foreground/60" />
                <span>{f.name.replace(".md", "")}</span>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    closeFile(tabId, pane);
                  }}
                  className="p-0.5 rounded-full hover:bg-secondary text-muted-foreground hover:text-foreground transition-all ml-1.5"
                >
                  <X className="w-3 h-3" />
                </button>
              </div>
            );
          })}
          
          {/* Active Settings Tab */}
          <div className="h-9 px-3 flex items-center gap-1.5 border-r border-border/40 text-xs font-medium cursor-pointer bg-background text-foreground border-b-2 border-b-primary shrink-0">
            <Settings className="w-3.5 h-3.5 text-primary" />
            <span>Configuración</span>
            <button
              onClick={() => setPaneViewType(paneState.openTabs.length > 0 ? "editor" : "welcome", pane)}
              className="p-0.5 rounded-full hover:bg-secondary text-muted-foreground hover:text-foreground transition-all ml-1.5"
            >
              <X className="w-3 h-3" />
            </button>
          </div>
        </div>

        {/* Tab actions */}
        <div className="flex items-center gap-1 px-2 border-l border-border/40 shrink-0">
          <button 
            onClick={() => {
              const newId = createFile("Nota sin título");
              openFile(newId, pane);
            }}
            className="p-1 hover:bg-secondary/50 rounded text-muted-foreground hover:text-foreground transition-all"
            title="Nueva nota"
          >
            <Plus className="w-4 h-4" />
          </button>
          
          <button 
            onClick={() => setIsSplit(!isSplit)}
            className={cn(
              "p-1 hover:bg-secondary/50 rounded transition-all",
              isSplit ? "text-primary bg-primary/10" : "text-muted-foreground hover:text-foreground"
            )}
            title={isSplit ? "Unir paneles" : "Dividir panel"}
          >
            <Columns className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Settings Content */}
      <div className="flex-1 p-6 md:p-8 overflow-y-auto custom-scrollbar">
        <div className="max-w-2xl mx-auto space-y-8 select-none">
          <div>
            <h2 className="text-xl font-bold text-foreground">Preferencias</h2>
            <p className="text-xs text-muted-foreground mt-1">Configura el comportamiento visual y de edición de tu entorno de trabajo.</p>
          </div>

          {/* Theme Section */}
          <div className="p-5 rounded-2xl bg-secondary/10 border border-border/30 space-y-4">
            <h3 className="text-xs font-bold uppercase tracking-wider text-primary flex items-center gap-1.5">
              <Database className="w-4 h-4" />
              Base de Datos Local
            </h3>
            <div className="text-xs text-foreground/80 space-y-2">
              <p>Tu workspace se almacena automáticamente en el almacenamiento local del navegador para acceso instantáneo offline.</p>
              <div className="flex items-center gap-3 pt-2">
                <button 
                  onClick={() => setShowResetConfirm(true)}
                  className="flex items-center gap-1 px-3 py-2 border border-destructive/20 hover:bg-destructive/10 rounded-xl text-xs font-bold text-destructive transition-all"
                >
                  <Trash2 className="w-3.5 h-3.5" />
                  Restablecer Base de Datos
                </button>
              </div>
            </div>
          </div>

          {/* Typography configuration */}
          <div className="p-5 rounded-2xl bg-secondary/10 border border-border/30 space-y-4">
            <h3 className="text-xs font-bold uppercase tracking-wider text-primary flex items-center gap-1.5">
              <HelpCircle className="w-4 h-4" />
              Configuración de Editor
            </h3>
            
            <div className="space-y-3">
              <div className="flex flex-col gap-1.5">
                <label className="text-xs font-semibold text-foreground/85">Familia tipográfica</label>
                <select 
                  value={editorFont}
                  onChange={e => setEditorFont(e.target.value)}
                  className="w-full max-w-xs px-3 py-2 bg-background border border-border/40 rounded-xl text-xs outline-none text-foreground"
                >
                  <option value="font-sans">San Serif (Inter)</option>
                  <option value="font-serif">Serif (Playfair)</option>
                  <option value="font-mono">Monospace (Fira Code)</option>
                </select>
              </div>

              <div className="flex flex-col gap-1.5">
                <label className="text-xs font-semibold text-foreground/85">Tamaño de letra: {fontSize}px</label>
                <input 
                  type="range" 
                  min="12" 
                  max="20" 
                  value={fontSize} 
                  onChange={e => setFontSize(e.target.value)}
                  className="w-full max-w-xs accent-primary h-1 bg-secondary rounded-lg appearance-none cursor-pointer"
                />
              </div>

              <div className="pt-2">
                <button 
                  onClick={handleSaveSettings}
                  className="flex items-center gap-1.5 px-4 py-2 bg-primary text-white rounded-xl text-xs font-bold hover:bg-primary/95 transition-all shadow-md shadow-primary/10"
                >
                  {successMsg ? <Check className="w-3.5 h-3.5" /> : null}
                  {successMsg ? "Guardado" : "Guardar Preferencias"}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <ConfirmDialog
        open={showResetConfirm}
        onOpenChange={setShowResetConfirm}
        title="Restablecer base de datos local"
        description="Esto borrará todos tus cambios locales y recargará la página. Los archivos en el servidor no se verán afectados."
        confirmText="Sí, restablecer"
        cancelText="Cancelar"
        variant="destructive"
        onConfirm={() => {
          localStorage.removeItem("wiki_arsenal_workspace_v2");
          window.location.reload();
        }}
      />
    </div>
  );
}
